import type { AdditionalField } from '@views/Lists/ListsTypes.d';
import type { DynamicSubjectRow } from '@cwd/Views/Issues/dynamicSubject';

export const STATISTICS_EVENT_TYPES = [
    { key: 'sent', contactType: 'Sent', color: '#90d850', hoverColor: '#a0e860' },
    { key: 'received', contactType: 'Received', color: '#51dbb5', hoverColor: '#61eac4' },
    { key: 'openedorclicked', contactType: 'OpenClicked', color: '#9c6bdf', hoverColor: '#a67ae2' },
    { key: 'opened', contactType: 'Opened', color: '#5bc6dc', hoverColor: '#57cee8' },
    { key: 'clicked', contactType: 'Clicked', color: '#db51bd', hoverColor: '#ea61cd' },
    { key: 'conversion', contactType: 'Converted', color: '#4c8fe0', hoverColor: '#5c9ef0' },
    { key: 'bounced', contactType: 'Bounced', color: '#f0c550', hoverColor: '#fdd35e' },
    { key: 'unsubscribed', contactType: 'Unsubscribed', color: '#ee5b56', hoverColor: '#fc6964' },
    { key: 'inactive', contactType: 'Inactive', color: '#bbb', hoverColor: '#c5c5c5' },
] as const;

export function getStatisticsLabelMap(): Record<string, string> {
    return {
        sent: $Resources.IssuesSentLabel,
        received: $Resources.IssuesReceivedPercentageLabel,
        openedorclicked: $Resources.IssuesOpenClickedPercentageLabel,
        opened: $Resources.IssuesOpenedPercentageLabel,
        clicked: $Resources.IssuesClickedPercentageLabel,
        conversion: $Resources.IssuesConvertedPercentageLabel,
        bounced: $Resources.IssuesBouncedPercentageLabel,
        unsubscribed: $Resources.IssuesUnsubscribedPercentageLabel,
        inactive: $Resources.IssuesInactivePercentageLabel,
    };
}

function negateOperator(op: string): string {
    const map: Record<string, string> = {
        eq: 'ne',
        ne: 'eq',
        contains: 'notContains',
        notcontains: 'contains',
        empty: 'notEmpty',
        notempty: 'empty',
    };
    return map[op.toLowerCase()] ?? op;
}

// Map dynamic-subject operator names to the ContactFilter operator keys.
// Dynamic subject uses lowercase ('notcontains', 'notempty'); ContactFilter uses camelCase.
const DYNAMIC_SUBJECT_OP_MAP: Record<string, string> = {
    eq: 'eq',
    ne: 'ne',
    contains: 'contains',
    notcontains: 'notContains',
    empty: 'empty',
    notempty: 'notEmpty',
};

export interface StatisticsPresetFilter {
    fieldName: string;
    operator: string;
    text: string;
}

export function buildDynamicSubjectPresetFilters(rows: DynamicSubjectRow[], availableFields: AdditionalField[], negate: boolean): StatisticsPresetFilter[] {
    const result: StatisticsPresetFilter[] = [];
    for (const row of rows) {
        const fieldName = row.property.replace(/^Contact\./, '');
        if (!availableFields.some((f) => f.name === fieldName)) continue;
        const rawOp = negate ? negateOperator(row.operator) : row.operator;
        const operator = DYNAMIC_SUBJECT_OP_MAP[rawOp.toLowerCase()] ?? rawOp;
        result.push({ fieldName, operator, text: row.value });
    }
    return result;
}

export function getIssueStatisticsContactListHeadings(): Record<string, string> {
    return {
        Sent: $Resources.StatisticsContactListHeadingSent,
        Received: $Resources.StatisticsContactListHeadingReceived,
        Opened: $Resources.StatisticsContactListHeadingOpened,
        OpenClicked: $Resources.StatisticsContactListHeadingOpenClicked,
        Clicked: $Resources.StatisticsContactListHeadingClicked,
        Converted: $Resources.StatisticsContactListHeadingConverted,
        Bounced: $Resources.StatisticsContactListHeadingBounced,
        Unsubscribed: $Resources.StatisticsContactListHeadingUnsubscribed,
        Inactive: $Resources.StatisticsContactListHeadingInactive,
        ClickedLink: $Resources.StatisticsContactListClickedLinkHeading,
        Shared: $Resources.StatisticsContactListSharedHeading,
    };
}
